/**
 * PEOPLE & ACCESS — who works here, and what they hold.
 *
 *   "I want to one day be able to change the super-admin role email address
 *    from 'info' to 'eddie' ... and also change a password if it's been
 *    forgotten, as well as update any other staff information on the portal."
 *
 * All three are here, and one of them behaves differently from how it looks.
 *
 * THE ADDRESS IS NOT A FIELD YOU SAVE.
 *
 * Changing it is two updates — auth.users and admin_users — and only Supabase
 * can make the first. So it is a request, not an edit: a confirmation goes to
 * the NEW address, and nothing changes until the link in it is clicked. The
 * screen says exactly that, because a form that looks like a text field and
 * behaves like an email round-trip is a form people press twice.
 *
 * And it is your OWN address only. supabase.auth.updateUser changes the
 * signed-in user, which happens to be the rule we want: somebody else's address
 * is changed by that person, from their own session.
 *
 * WHAT THE SCREEN DOES NOT DECIDE
 *
 * Every refusal below comes from the database and is shown in its own words:
 * you cannot suspend yourself, you cannot change your own permissions, you
 * cannot grant an MFA-required permission to an unenrolled account, you cannot
 * grant an export permission at all without a time-boxed role. The buttons that
 * would obviously fail are disabled as a courtesy; the rules are not here.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  O, SANS, Card, StatusPill, PrimaryCTA, QuietButton, Notice, LoadingPanel,
  Field, textInputStyle,
} from '../ui';
import { isStaffEmail, emailProblem } from '../lib/staffCredentials';
import {
  fetchStaffDirectory, fetchGrantablePermissions, updateStaffMember, withdrawStaffInvitation,
  setStaffPermissions, requestEmailChange, sendPasswordReset,
  inviteStaffMember, staffInviteProblem, STAFF_DEPARTMENTS,
} from '../lib/people';
import { resetStaffMfa } from '../lib/mfa';

const MIN_REASON = 8;

// htmlFor and a matching id, because a <label> not tied to its input is not a
// label -- a screen reader reads an unnamed text box, and the test that caught
// this failed for the same reason a person using one would have.
let reasonSeq = 0;
function Reason({ value, onChange, label = 'Why?' }) {
  const [id] = useState(() => `reason-${(reasonSeq += 1)}`);
  return (
    <Field label={label} htmlFor={id}
           hint={`At least ${MIN_REASON} characters. Written to the append-only log.`}>
      <input id={id} value={value} onChange={(e) => onChange(e.target.value)}
             style={textInputStyle(false)} />
    </Field>
  );
}

// ---------------------------------------------------------------------------

function MyAddress({ me, onDone }) {
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const problem = next ? emailProblem(next) : null;

  async function submit() {
    setBusy(true);
    setError(null);
    const res = await requestEmailChange(next);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    setSent(true);
    if (onDone) onDone();
  }

  return (
    <Card>
      <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink }}>Your own address</h3>
      <p style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
        Signed in as <strong>{me.email}</strong>.
      </p>

      {sent ? (
        <div style={{ marginTop: 12 }}>
          {/* TWO CONFIRMATIONS, NOT ONE, AND THIS SAID ONE.
              Secure email change is ON for this project, so Supabase sends a
              confirmation to the CURRENT address as well as the new one and the
              change commits only when BOTH are opened. The founder: "I enter
              the eddie@ email ... and the confirmation email with the link DOES
              arrive in the info@ inbox" -- that is the one addressed to his
              current address, working exactly as designed, with nothing
              anywhere telling him a second was outstanding.

              The To: line is named because eddie@ is an ALIAS of info@, so both
              land in one mailbox looking identical. Which is which is only
              visible on that header. */}
          <Notice tone="info">
            Confirmations are on their way to <strong>{next}</strong> and to{' '}
            <strong>{me.email}</strong>. Orchid needs <strong>both</strong> opened before
            the change takes effect — until then you keep signing in with {me.email}.
            If one address forwards to the other, check the <strong>To:</strong> line to
            tell the two apart.
          </Notice>
        </div>
      ) : (
        <>
          <div style={{ marginTop: 12 }}>
            <Field label="Change it to" htmlFor="new-address" error={problem}>
              <input
                id="new-address"
                type="email"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="eddie@orchidholdings.co.za"
                style={textInputStyle(Boolean(problem))}
              />
            </Field>
          </div>

          <p style={{ fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
            A confirmation goes to the new address. Nothing changes until you click the
            link in it — which is how the portal knows the address is really yours. Your
            staff account follows automatically at that moment.
          </p>

          {error && <div style={{ marginTop: 12 }}><Notice tone="critical">{error}</Notice></div>}

          <div style={{ marginTop: 14, maxWidth: 260 }}>
            <PrimaryCTA onClick={submit} busy={busy} disabled={busy || !isStaffEmail(next)}>
              Send the confirmation
            </PrimaryCTA>
          </div>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function Person({ person, permissions, onChanged }) {
  const [panel, setPanel] = useState(null);      // 'details' | 'permissions'
  const [reason, setReason] = useState('');
  const [fullName, setFullName] = useState(person.full_name || '');
  const [jobTitle, setJobTitle] = useState(person.job_title || '');
  const [held, setHeld] = useState(person.permissions || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const tooShort = reason.trim().length < MIN_REASON;

  const run = async (fn) => {
    setBusy(true); setError(null); setNote(null);
    const res = await fn();
    setBusy(false);
    if (res.error) { setError(res.error); return false; }
    setReason('');
    if (onChanged) onChanged();
    return true;
  };

  const saveDetails = () => run(() => updateStaffMember({
    id: person.id, reason: reason.trim(), fullName, jobTitle,
  })).then((ok) => { if (ok) { setPanel(null); setNote('Saved.'); } });

  const savePermissions = () => run(() => setStaffPermissions({
    id: person.id, permissions: held, reason: reason.trim(),
  })).then((ok) => { if (ok) { setPanel(null); setNote('Permissions updated.'); } });

  const suspend = () => run(() => updateStaffMember({
    id: person.id, reason: reason.trim(), status: 'suspended',
  })).then((ok) => { if (ok) { setPanel(null); setNote('Suspended.'); } });

  const reinstate = () => run(() => updateStaffMember({
    id: person.id, reason: reason.trim(), status: 'active',
  })).then((ok) => { if (ok) { setPanel(null); setNote('Reinstated.'); } });

  /**
   * Remove a seat nobody ever used.
   *
   * Offered only where has_login is false, because that is the only case the
   * database will allow — and offering a control that is going to be refused
   * teaches people to distrust the whole row of them. Somebody who HAS signed
   * in and acted is suspended instead, and the button beside this one says so.
   */
  const withdraw = () => run(() => withdrawStaffInvitation({
    id: person.id, reason: reason.trim(),
  })).then((ok) => { if (ok) { setPanel(null); setNote('Removed.'); } });

  const reset = async () => {
    setBusy(true); setError(null); setNote(null);
    const res = await sendPasswordReset(person.email);
    setBusy(false);
    // Deliberately the same message either way: a different one would confirm
    // whether that address has an account, which is the thing the single
    // sign-in error exists to avoid.
    setNote(res.error ? null : 'A reset link has been sent to that address.');
    if (res.error) setError(res.error);
  };

  /**
   * "a human capital senior must be given permission to, from within the
   *  Portal, remove the mfa authentication requirement for a staff member who
   *  lost their phone."
   *
   * Through the same run() as every other write on this row, so the refusals
   * arrive in the database's own words. There are three, and they read
   * differently on purpose: no permission, not your own, and a reason too short
   * to be one.
   */
  const removeAuthenticator = () => run(() => resetStaffMfa(person.id, reason.trim()))
    .then((ok) => {
      if (!ok) return;
      setPanel(null);
      setNote(`${person.full_name} can set up a new authenticator now. `
            + 'They hold no MFA permission until they do.');
    });

  const toggle = (key) => setHeld((h) =>
    h.includes(key) ? h.filter((k) => k !== key) : [...h, key]);

  return (
    <Card>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 9 }}>
        <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink }}>{person.full_name}</h3>
        {person.is_me && <StatusPill tone="quiet">You</StatusPill>}
        {/* Script 97. The database refuses to suspend, rename, strip or delete
            this row, so the row says so before somebody reaches for a control
            that is going to fail. */}
        {person.is_founding_seat && <StatusPill tone="green">Founding seat</StatusPill>}
        <StatusPill tone={person.status === 'active' ? 'green' : 'warning'}>{person.status}</StatusPill>
        {/* Read from auth.mfa_factors by fn_staff_directory since script 98,
            not from a column that was written by nothing for months. */}
        {!person.mfa_enrolled && <StatusPill tone="warning">No authenticator</StatusPill>}
        {!person.has_login && <StatusPill tone="quiet">No login yet</StatusPill>}
      </div>

      <p style={{ marginTop: 5, fontSize: 12.5, color: O.inkMuted }}>
        {person.email}{person.job_title ? ` · ${person.job_title}` : ''}
        {person.oid_admin_id ? ` · ${person.oid_admin_id}` : ''}
      </p>

      <p style={{ marginTop: 8, fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
        {person.permissions?.includes('all')
          ? 'Holds every permission.'
          : `${person.permissions?.length || 0} permission${person.permissions?.length === 1 ? '' : 's'}`}
        {person.roles_held?.length ? ` · roles: ${person.roles_held.join(', ')}` : ''}
      </p>

      {note && <div style={{ marginTop: 12 }}><Notice tone="info">{note}</Notice></div>}
      {error && <div style={{ marginTop: 12 }}><Notice tone="critical">{error}</Notice></div>}

      {panel === 'details' && (
        <div style={{ marginTop: 14 }}>
          <Field label="Full name"><input value={fullName} onChange={(e) => setFullName(e.target.value)} style={textInputStyle(false)} /></Field>
          <Field label="Job title"><input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} style={textInputStyle(false)} /></Field>
          <Reason value={reason} onChange={setReason} />
          <div style={{ display: 'flex', gap: 10 }}>
            <QuietButton onClick={() => setPanel(null)}>Cancel</QuietButton>
            <PrimaryCTA onClick={saveDetails} busy={busy} disabled={busy || tooShort}>Save</PrimaryCTA>
          </div>
        </div>
      )}

      {panel === 'permissions' && (
        <div style={{ marginTop: 14 }}>
          <p style={{ fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint, marginBottom: 10 }}>
            An export permission cannot be granted here at all — it needs a time-boxed
            role — and an MFA permission is refused for an account that is not enrolled.
            Both refusals come from the database.
          </p>
          <div style={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${O.hairline}`, borderRadius: 11, padding: 10, marginBottom: 14 }}>
            {permissions.map((p) => (
              <label key={p.permission_key} style={{
                display: 'flex', alignItems: 'flex-start', gap: 9, padding: '6px 2px',
                fontSize: 12.5, color: O.ink, cursor: p.is_export ? 'not-allowed' : 'pointer',
                opacity: p.is_export ? 0.45 : 1,
              }}>
                <input
                  type="checkbox"
                  checked={held.includes(p.permission_key)}
                  disabled={p.is_export}
                  onChange={() => toggle(p.permission_key)}
                />
                <span>
                  <code>{p.permission_key}</code>
                  {p.card_title && <span style={{ color: O.inkFaint }}> · {p.card_title}</span>}
                  {p.requires_mfa && <span style={{ color: O.warning }}> · MFA</span>}
                  {p.is_export && <span style={{ color: O.critical }}> · export, role only</span>}
                </span>
              </label>
            ))}
          </div>
          <Reason value={reason} onChange={setReason} label="Why this change?" />
          <div style={{ display: 'flex', gap: 10 }}>
            <QuietButton onClick={() => setPanel(null)}>Cancel</QuietButton>
            <PrimaryCTA onClick={savePermissions} busy={busy} disabled={busy || tooShort}>
              Set permissions
            </PrimaryCTA>
          </div>
        </div>
      )}

      {panel === 'status' && (
        <div style={{ marginTop: 14 }}>
          <Reason value={reason} onChange={setReason}
                  label={person.status === 'active' ? 'Why suspend this account?' : 'Why reinstate it?'} />
          <div style={{ display: 'flex', gap: 10 }}>
            <QuietButton onClick={() => setPanel(null)}>Cancel</QuietButton>
            <PrimaryCTA
              onClick={person.status === 'active' ? suspend : reinstate}
              busy={busy}
              disabled={busy || tooShort}
              style={person.status === 'active' ? { background: O.critical, boxShadow: 'none' } : undefined}
            >
              {person.status === 'active' ? 'Suspend' : 'Reinstate'}
            </PrimaryCTA>
          </div>
        </div>
      )}

      {panel === 'withdraw' && (
        <div style={{ marginTop: 14 }}>
          <p style={{ fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted, marginBottom: 10 }}>
            {person.full_name} has never signed in, so nothing on the platform names them and
            nothing is lost. This deletes the seat outright — adding them again starts a fresh
            invitation. Their OID is not lost with it: since script 95 it belongs to the
            person, so if they gave an ID number they are handed the same one back.
          </p>
          <Reason value={reason} onChange={setReason} label="Why remove this seat?" />
          <div style={{ display: 'flex', gap: 10 }}>
            <QuietButton onClick={() => setPanel(null)}>Cancel</QuietButton>
            <PrimaryCTA
              onClick={withdraw}
              busy={busy}
              disabled={busy || tooShort}
              style={{ background: O.critical, boxShadow: 'none' }}
            >Remove this seat</PrimaryCTA>
          </div>
        </div>
      )}

      {panel === 'mfa' && (
        <div style={{ marginTop: 14 }}>
          {/* ORANGE, NOT RED. Removing somebody's authenticator because they
              lost their phone is help, not a sanction — and it opens nothing:
              every permission that needs MFA closes the moment the factor is
              gone and reopens only when they enrol again. Dressing it as a
              destructive act would make Human Capital hesitate over the one
              thing standing between a colleague and their job. */}
          <p style={{ fontSize: 12.5, lineHeight: 1.6, color: '#7A4E06' }}>
            This removes the authenticator on <strong>{person.full_name}</strong>&rsquo;s
            account so they can set up a new one on a new phone.
          </p>
          <p style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
            It grants them nothing. Until they have set one up again they hold no
            permission that requires MFA — the database reads the authenticator itself,
            so those permissions close on their own and reopen when they enrol. They
            will see the setup panel the next time they open the portal.
          </p>
          <Reason value={reason} onChange={setReason}
                  label="Why is this authenticator being removed?" />
          <div style={{ display: 'flex', gap: 10 }}>
            <QuietButton onClick={() => setPanel(null)}>Cancel</QuietButton>
            <PrimaryCTA onClick={removeAuthenticator} busy={busy} disabled={busy || tooShort}>
              Remove the authenticator
            </PrimaryCTA>
          </div>
        </div>
      )}

      {!panel && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
          <div style={{ width: 130 }}>
            <QuietButton onClick={() => { setNote(null); setError(null); setPanel('details'); }}>
              Edit details
            </QuietButton>
          </div>
          <div style={{ width: 150 }}>
            {/*
              Disabled for yourself because the database refuses it: every grant
              needs a named human accountable for it, and that cannot be the
              person receiving it. Saying so on the button beats saying it in an
              error.
            */}
            <QuietButton
              onClick={() => {
                if (person.is_me || person.is_founding_seat) return;
                setNote(null); setError(null); setPanel('permissions');
              }}
              style={person.is_me || person.is_founding_seat
                ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              {person.is_me ? 'Permissions — not your own'
                : person.is_founding_seat ? 'Permissions — always all'
                : 'Permissions'}
            </QuietButton>
          </div>
          <div style={{ width: 150 }}>
            <QuietButton onClick={reset}>Send a reset link</QuietButton>
          </div>
          <div style={{ width: 190 }}>
            {/* NOT ON YOURSELF, and not on somebody with nothing to remove.
                Script 118 refuses both, so a button that rendered live and then
                failed would teach people the row is unreliable — the same
                reasoning as the suspend button above. */}
            <QuietButton
              onClick={() => {
                if (person.is_me || !person.mfa_enrolled) return;
                setNote(null); setError(null); setPanel('mfa');
              }}
              style={person.is_me || !person.mfa_enrolled
                ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              {person.is_me ? 'Authenticator — not your own'
                : !person.mfa_enrolled ? 'No authenticator set up'
                : 'Remove authenticator'}
            </QuietButton>
          </div>
          <div style={{ width: 130 }}>
            {/* "nobody must be able to suspend the original
                info@orchidholdings.co.za - it's the master account and has all
                permissions." Script 97 makes that a trigger, so this button
                would fail rather than mislead; a control that renders and then
                refuses teaches people the whole row is unreliable. */}
            <QuietButton
              onClick={() => {
                if (person.is_me || (person.is_founding_seat && person.status === 'active')) return;
                setNote(null); setError(null); setPanel('status');
              }}
              style={person.is_me || (person.is_founding_seat && person.status === 'active')
                ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              {person.is_me
                ? 'Suspend — not your own'
                : person.is_founding_seat && person.status === 'active'
                  ? 'Suspend — never this one'
                  : person.status === 'active' ? 'Suspend' : 'Reinstate'}
            </QuietButton>
          </div>
          {/* REMOVE, AND ONLY WHERE IT CAN WORK.
              "I should be able to delete a staff user when someone leaves the
              company." True — and the reason there was no delete is also true:
              twenty-seven tables name the staff member who did each thing, and
              an audit trail that forgets who approved something is not one.
              A seat nobody ever signed into is named by nothing, so there is
              nothing to protect. That is the only case offered here, because a
              control that is going to be refused teaches people to distrust the
              whole row of them. */}
          {!person.is_me && !person.has_login && !person.is_founding_seat && (
            <div style={{ width: 130 }}>
              <QuietButton
                onClick={() => { setNote(null); setError(null); setPanel('withdraw'); }}
              >Remove</QuietButton>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

/**
 * Adding somebody to the staff.
 *
 *   "I'm still not able to add staff in the Orchid Portal."
 *
 * Because there was no create. Script 54 built read, amend and grant, and the
 * only staff account in existence was the one seeded by hand — so there had
 * never been a way to add the second person.
 *
 * WHY THE WORDING SAYS "ADDED", NOT "CREATED"
 *
 * Because no account is created here. A portal cannot mint a credential — that
 * needs the service role, which is not in this bundle and should not be — so
 * this writes an invitation with no login attached. It becomes an account the
 * moment that person signs up with the address, and until then it can open
 * nothing: the portal matches auth.uid() against auth_user_id, and a null one
 * matches nobody.
 *
 * PERMISSIONS ARE NOT SET HERE, ON PURPOSE.
 *
 * The founder asked to add somebody and grant them a set in one go. Splitting
 * it is not obstruction: canon Part Three requires "a named human accountable
 * for granting it", and the existing grant path already logs the grantor,
 * refuses self-elevation and demands a reason. Granting inside the invite would
 * be a second path with its own idea of those rules. So this adds the person
 * and then points at their card, where the whole permission list is already
 * waiting — including copying the founder's own set.
 */
function AddStaff({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [role, setRole] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [touched, setTouched] = useState(false);

  const problem = staffInviteProblem({ fullName, email, role });
  const tooShort = reason.trim().length < MIN_REASON;

  /**
   * When to show what is wrong.
   *
   * NOT only after a submit attempt, which is the trap a first version of this
   * fell into: the button is disabled while the address is wrong, so pressing
   * it -- the thing that would have revealed the reason -- is impossible. That
   * is a dead end with no explanation, and it is the same shape as every "I
   * literally cannot" report on this platform.
   *
   * So a field explains itself as soon as there is something in it to be wrong
   * about, and stays quiet while it is merely empty.
   */
  const nameProblem = String(fullName).trim().length < 2 ? 'Who is this?' : null;
  const emailProblemNow = email.trim().length > 0 && problem
    && !problem.startsWith('Who') && !problem.startsWith('Which')
    ? problem
    : null;

  const submit = async () => {
    setTouched(true);
    if (problem || tooShort) return;
    setBusy(true);
    setError(null);
    const res = await inviteStaffMember({
      fullName, email, role, jobTitle, reason: reason.trim(),
    });
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    setDone(res.data);
    setFullName(''); setEmail(''); setJobTitle(''); setRole(''); setReason(''); setTouched(false);
    onAdded();
  };

  if (!open) {
    return (
      <Card style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: 0 }}>
              Add a staff member
            </h3>
            <p style={{ fontSize: 12, color: O.inkMuted, margin: '4px 0 0', lineHeight: 1.6 }}>
              They sign in with their own Orchid address. You grant permissions afterwards,
              from their card below.
            </p>
          </div>
          <PrimaryCTA onClick={() => { setOpen(true); setDone(null); }} style={{ width: 'auto' }}>
            Add someone
          </PrimaryCTA>
        </div>
        {done && (
          <Notice tone="green">
            {done.message} Their card is below — open it to grant permissions.
          </Notice>
        )}
      </Card>
    );
  }

  return (
    <Card style={{ marginBottom: 18 }}>
      <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: '0 0 4px' }}>
        Add a staff member
      </h3>
      <p style={{ fontSize: 12, color: O.inkMuted, margin: '0 0 14px', lineHeight: 1.6 }}>
        This adds them to the directory with no permissions and no login. They get access by
        signing up with the address below; you grant what they may do from their card.
      </p>

      {error && <Notice tone="critical">{error}</Notice>}

      <Field label="Their full name" htmlFor="new-staff-name"
             error={touched ? nameProblem : null}>
        <input id="new-staff-name" value={fullName}
               onChange={(e) => setFullName(e.target.value)}
               style={textInputStyle(Boolean(touched && nameProblem))} />
      </Field>

      <Field label="Their Orchid address" htmlFor="new-staff-email"
             hint="Staff identity is separate from user identity — this must be @orchidholdings.co.za."
             error={emailProblemNow}>
        <input id="new-staff-email" type="email" value={email}
               onChange={(e) => setEmail(e.target.value)}
               placeholder="name@orchidholdings.co.za"
               style={textInputStyle(Boolean(emailProblemNow))} />
      </Field>

      <Field label="Which part of Orchid" htmlFor="new-staff-role"
             hint="Where they work, not what they may do — permissions are granted separately."
             error={touched && !role ? 'Choose a department.' : null}>
        <select id="new-staff-role" value={role}
                onChange={(e) => setRole(e.target.value)}
                style={textInputStyle(Boolean(touched && !role))}>
          <option value="">Choose…</option>
          {STAFF_DEPARTMENTS.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Job title" htmlFor="new-staff-title" hint="Optional.">
        <input id="new-staff-title" value={jobTitle}
               onChange={(e) => setJobTitle(e.target.value)}
               style={textInputStyle(false)} />
      </Field>

      <Reason value={reason} onChange={setReason} label="Why are you adding them?" />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <PrimaryCTA onClick={submit} busy={busy} disabled={Boolean(problem) || tooShort}
                    style={{ flex: 2, minWidth: 180 }}>
          {busy ? 'Adding…' : 'Add them'}
        </PrimaryCTA>
        <QuietButton onClick={() => { setOpen(false); setError(null); setTouched(false); }}
                     style={{ flex: 1, minWidth: 120 }}>
          Cancel
        </QuietButton>
      </div>
    </Card>
  );
}

export default function StaffDirectory() {
  const [rows, setRows] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const [dir, perms] = await Promise.all([fetchStaffDirectory(), fetchGrantablePermissions()]);
    if (dir.error) { setError(dir.error); setRows([]); return; }
    setError(null);
    setRows(dir.data);
    // Not an error if this one is refused: it means this person may see the
    // directory and may not grant, which is a real and intended combination.
    setPermissions(perms.error ? [] : perms.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const me = (rows || []).find((r) => r.is_me);

  if (rows === null && !error) return <LoadingPanel message="Reading the directory…" />;

  return (
    <div style={{ fontFamily: SANS }}>
      {error && <Notice tone="critical">{error}</Notice>}

      {me && <div style={{ marginBottom: 18 }}><MyAddress me={me} onDone={load} /></div>}

      {/* Only for somebody who may actually add. Drawing the panel for a reader
          who holds view_staff_directory and not manage_staff_accounts offers a
          button whose only outcome is a permission error. */}
      {me?.permissions?.some((p) => p === 'manage_staff_accounts' || p === 'all')
        && <AddStaff onAdded={load} />}

      {rows && rows.length > 0 && (
        <>
          <h2 style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: O.inkFaint, marginBottom: 12,
          }}>Staff · {rows.length}</h2>
          <div style={{ display: 'grid', gap: 12 }}>
            {rows.map((p) => (
              <Person key={p.id} person={p} permissions={permissions} onChanged={load} />
            ))}
          </div>
        </>
      )}

      {rows && rows.length === 0 && !error && (
        <Card><p style={{ fontSize: 13, color: O.inkMuted }}>
          You do not hold the permission to see the staff directory.
        </p></Card>
      )}
    </div>
  );
}
